import PropTypes from 'prop-types';
import React from 'react';

import ChooseDonation from './ChooseDonation';
import ChooseSeatedTicketTypes from './ChooseSeatedTicketTypes';
import ChooseSeatedAddOns from './ChooseSeatedAddOns';
import { trackXPixelEvent, X_PIXEL_ADD_TO_CART } from '../utils/xPixel';
import { upcomingPriceText } from '../utils/upcomingPrice';
import { priceOptionGroupForItem, quantityOptionsWithinOrderLimit, rowLabel } from '../utils/priceOptions';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const seatingChartItemVisible = (checkoutGroupItem, seatedAddOns) =>
  Boolean(checkoutGroupItem.extra) &&
  Boolean(checkoutGroupItem.extra.seatsio_event_id) &&
  (seatedAddOns || []).length > 0;

const formatPrice = (calculatedPrice, free) => {
  if (free) return 'FREE';
  const num = parseFloat(calculatedPrice);
  if (Number.isNaN(num)) return null;
  return formatter.format(num);
};

// Walks `options` to find the neighbor on either side of `value`. Falls back
// to `value ± 1` only if the current value isn't in the options list (which
// shouldn't happen, but keeps a click from no-op'ing if backend data drifts).
const neighbor = (options, value, direction) => {
  const list = options || [];
  const idx = list.indexOf(value);
  if (idx === -1) return value + direction;
  const next = idx + direction;
  if (next < 0 || next >= list.length) return null;
  return list[next];
};

const QuantityStepper = ({ value, options, ariaLabel, onChange }) => {
  const current = Number(value) || 0;
  const list = options || [];
  const max = list.length > 0 ? list[list.length - 1] : 0;

  // Plus / minus rendered as plain text glyphs (U+002B / U+2212) instead of
  // FontAwesome <i> tags — the admin builder previews this widget inside an
  // iframe whose <head> only inherits <link>/<style> tags, not the Kit JS
  // script that swaps <i> nodes into SVGs. Text glyphs render everywhere.
  if (current <= 0) {
    return (
      <button
        type="button"
        className="cwb-public-qty-add"
        aria-label={`Add ${ariaLabel}`}
        onClick={() => {
          const first = list.find((q) => q > 0);
          if (first != null) onChange(first);
        }}>
        +
      </button>
    );
  }

  const dec = neighbor(list, current, -1);
  const inc = neighbor(list, current, +1);

  return (
    <div className="cwb-public-qty-stepper" role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className="cwb-public-qty-btn"
        aria-label={`Decrease ${ariaLabel}`}
        onClick={() => onChange(dec == null ? 0 : dec)}>
        −
      </button>
      <span className="cwb-public-qty-count" aria-live="polite">{current}</span>
      <button
        type="button"
        className="cwb-public-qty-btn"
        aria-label={`Increase ${ariaLabel}`}
        disabled={current >= max}
        onClick={() => inc != null && onChange(inc)}>
        +
      </button>
    </div>
  );
};

const SoldOutBadge = () => (
  <span className="cwb-public-badge sold-out">SOLD OUT</span>
);

const StatusText = ({ children }) => (
  <span className="cwb-public-item-status">{children}</span>
);

const CustomCheckoutGroupItemRow = ({
  team,
  adminPreview,
  checkoutGroupItem,
  checkoutGroupItems,
  visibleTickets,
  promoCode,
  ticketTypes,
  formatItemsForGoogleTagManager,
  postToParent,
  updateTicketTypeQuantity,
  buildTicketReservation,
  csrfToken,
  addOns,
  ticketReservation,
  userContext,
  confirm,
  addOnsChanged,
  addOnQuantityOptions,
  visibleAddOns,
  seatedAddOns,
  seatsIOPublicKey,
  selectedObjectLabel,
  selectedObjectType,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  onSelectionChange,
  no_orphan_seats,
  visibleTicketTypes,
  announceListEnabled,
  waitlistEnabled,
  ticketTypesChanged,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  debouncedBuildTicketReservation,
  feeRules,
  donationTitle,
  donationCoverProcessingText,
  startAtAddOns,
  membershipProducts,
  selectedMembershipProduct,
  selectedMembershipProductChanged,
}) => {
  const showAllInFee =
    confirm.enable_all_in_pricing
      && confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false;

  // === Donation ===
  if (checkoutGroupItem.enable_donation) {
    return (
      <div className="cwb-public-item-passthrough">
        <ChooseDonation
          team={team}
          csrfToken={csrfToken}
          confirm={confirm}
          ticketTypes={ticketTypes}
          ticketTypesChanged={ticketTypesChanged}
          promoCode={promoCode}
          buildTicketReservation={buildTicketReservation}
          debouncedBuildTicketReservation={debouncedBuildTicketReservation}
          addOns={addOns}
          ticketReservation={ticketReservation}
          userContext={userContext}
          donationCustomChargeType={donationCustomChargeType}
          donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
          feeRules={feeRules}
          donationTitle={donationTitle}
          donationCoverProcessingText={donationCoverProcessingText}
        />
      </div>
    );
  }

  // === Seated admission chart ===
  if (checkoutGroupItem.seatsio_event_id) {
    return (
      <div className="cwb-public-item-passthrough">
        <ChooseSeatedTicketTypes
          team={team}
          csrfToken={csrfToken}
          confirm={{ ...confirm, seatsio_event_id: checkoutGroupItem.seatsio_event_id }}
          ticketTypes={ticketTypes}
          promoCode={promoCode}
          ticketTypesChanged={ticketTypesChanged}
          buildTicketReservation={buildTicketReservation}
          userContext={userContext}
          addOns={addOns}
          ticketReservation={ticketReservation}
          postToParent={postToParent}
          formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
          seatsIOPublicKey={seatsIOPublicKey}
          seatsIOChart={seatsIOChart}
          seatsIOChartChanged={seatsIOChartChanged}
          seatsIOSelectedObjects={seatsIOSelectedObjects}
          seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
          selectedObjectLabel={selectedObjectLabel}
          selectedObjectType={selectedObjectType}
          visibleTicketTypes={visibleTicketTypes}
          onSelectionChange={onSelectionChange}
          announceListEnabled={announceListEnabled}
          waitlistEnabled={waitlistEnabled}
          adminPreview={adminPreview}
          ownsUnavailableState={false}
          no_orphan_seats={no_orphan_seats}
        />
      </div>
    );
  }

  // === Seated add-on chart ===
  if (checkoutGroupItem.add_on_seatsio_event_id) {
    if ((seatedAddOns || []).length === 0) return null;
    if ((visibleAddOns || []).length === 0) return null;

    return (
      <div className="cwb-public-item-passthrough">
        <ChooseSeatedAddOns
          team={team}
          csrfToken={csrfToken}
          confirm={{ ...confirm, add_on_seatsio_event_id: checkoutGroupItem.add_on_seatsio_event_id }}
          ticketTypes={ticketTypes}
          addOns={addOns}
          addOnsChanged={addOnsChanged}
          buildTicketReservation={buildTicketReservation}
          promoCode={promoCode}
          ticketReservation={ticketReservation}
          userContext={userContext}
          postToParent={postToParent}
          formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
          seatsIOPublicKey={seatsIOPublicKey}
          selectedObjectLabel={selectedObjectLabel}
          selectedObjectType={selectedObjectType}
          visibleAddOns={visibleAddOns}
          startAtAddOns={startAtAddOns}
          adminPreview={adminPreview}
        />
      </div>
    );
  }

  if (checkoutGroupItem.item_type === 'SeatingChartEvent') {
    if (!seatingChartItemVisible(checkoutGroupItem, seatedAddOns)) return null;

    return (
      <div className="cwb-public-item-passthrough">
        <ChooseSeatedAddOns
          team={team}
          csrfToken={csrfToken}
          confirm={confirm}
          ticketTypes={ticketTypes}
          addOns={addOns}
          addOnsChanged={addOnsChanged}
          buildTicketReservation={buildTicketReservation}
          promoCode={promoCode}
          ticketReservation={ticketReservation}
          userContext={userContext}
          postToParent={postToParent}
          formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
          seatsIOPublicKey={seatsIOPublicKey}
          selectedObjectLabel={selectedObjectLabel}
          selectedObjectType={selectedObjectType}
          checkoutGroupItem={checkoutGroupItem}
          visibleAddOns={visibleAddOns}
          startAtAddOns={startAtAddOns}
          adminPreview={adminPreview}
        />
      </div>
    );
  }

  // === Membership ===
  if (checkoutGroupItem.item_type === 'MembershipProduct') {
    const product = (membershipProducts || []).find((mp) => mp.id === checkoutGroupItem.item_id);
    if (!product) return null;

    const price = product.ticketing_price;
    const hasExisting = product.existing_membership != null;
    const selected = selectedMembershipProduct && selectedMembershipProduct.id === product.id;

    return (
      <div className="cwb-public-item-card">
        <div className="cwb-public-item-main">
          <div className="cwb-public-item-name">{product.name}</div>
          {price && (
            <div className="cwb-public-item-price">
              {price.price_to_currency}
              <span className="cwb-public-item-price-suffix">{` per ${price.interval_label}`}</span>
            </div>
          )}
          {product.description && product.description.length > 0 && (
            <div className="cwb-public-item-desc">{product.description}</div>
          )}
        </div>
        <div className="cwb-public-item-action">
          {hasExisting ? (
            <span className="cwb-public-badge active-member">
              <i className="fas fa-check-circle mr-1" />Active Member
            </span>
          ) : (
            <QuantityStepper
              ariaLabel="Membership Quantity"
              value={selected ? 1 : 0}
              options={[0, 1]}
              onChange={(quantity) => {
                if (quantity === 1) {
                  selectedMembershipProductChanged({ ...product, ticketing_price: price });
                } else {
                  selectedMembershipProductChanged(null);
                }
              }}
            />
          )}
        </div>
      </div>
    );
  }

  if (checkoutGroupItem.item_type === 'TicketType') {
    const priceGroup = priceOptionGroupForItem(checkoutGroupItem, checkoutGroupItems || [checkoutGroupItem], visibleTickets || []);
    if (!priceGroup) return null;
    const { heading, rows } = priceGroup;

    const cards = rows.map((ticketType) => {
      const quantityOptions = quantityOptionsWithinOrderLimit(rows, ticketType);
      let action;
      if (!adminPreview && ticketType.sales_paused) {
        action = <StatusText>Sales paused</StatusText>;
      } else if (!adminPreview && ticketType.scheduled) {
        action = <StatusText>{ticketType.wait_for_ticket_type && ticketType.wait_for_ticket_type.id ? 'Sales not started' : `Sales start ${ticketType.human_sales_start_date}`}</StatusText>;
      } else if (!adminPreview && ticketType.sales_ended) {
        action = <StatusText>Sale ended</StatusText>;
      } else if (!adminPreview && ticketType.sold_out) {
        action = <SoldOutBadge />;
      } else {
        const stepperOptions = quantityOptions.length > 0
          ? quantityOptions
          : (adminPreview ? Array.from({ length: 11 }, (_, i) => i) : []);
        action = (
          <QuantityStepper
            ariaLabel="Ticket Quantity"
            value={ticketType.quantity || 0}
            options={stepperOptions}
            onChange={(quantity) => {
              if (quantity > 0) {
                const updatedItem = { ...ticketType, quantity };
                const items = formatItemsForGoogleTagManager(confirm, promoCode, [updatedItem], []);
                postToParent('ga.add_to_cart', items);
                trackXPixelEvent(X_PIXEL_ADD_TO_CART, confirm, {
                  value: updatedItem.calculated_price * quantity,
                  numItems: quantity,
                });

                if (typeof window.rdt !== 'undefined') {
                  window.rdt('track', 'AddToCart', {
                    currency: 'USD',
                    value: updatedItem.calculated_price * quantity,
                    itemCount: quantity,
                    products: [{ id: confirm.id.toString(), name: confirm.title, category: 'Tickets' }],
                  });
                }
              }

              updateTicketTypeQuantity({ ticketTypeId: ticketType.id, quantity });

              const updated = ticketTypes.map((tt) =>
                tt.id === ticketType.id ? { ...tt, quantity } : tt
              );
              buildTicketReservation(csrfToken, confirm, updated, promoCode, addOns, ticketReservation, userContext);
            }}
          />
        );
      }

      return (
        <div key={ticketType.id} className={`cwb-public-item-card ${ticketType.sold_out || ticketType.sales_ended || ticketType.sales_paused ? 'is-disabled' : ''}`}>
          <div className="cwb-public-item-main">
            <div className="cwb-public-item-name">{heading ? rowLabel(heading, ticketType) : ticketType.name}</div>
            <div className="cwb-public-item-price">
              {formatPrice(ticketType.calculated_price, ticketType.free)}
              {showAllInFee && ticketType.all_in_price_description && (
                <span className="cwb-public-item-price-suffix">{` ${ticketType.all_in_price_description}`}</span>
              )}
            </div>
            {upcomingPriceText(ticketType, formatter) && (
              <div className="cwb-public-item-desc" data-testid={`upcoming-price-${ticketType.id}`}>{upcomingPriceText(ticketType, formatter)}</div>
            )}
            {ticketType.description && (
              <div
                className="cwb-public-item-desc"
                dangerouslySetInnerHTML={{ __html: ticketType.simple_format_description }}
              />
            )}
          </div>
          <div className="cwb-public-item-action">{action}</div>
        </div>
      );
    });

    return heading ? (
      <>
        <div className="cwb-public-item-name" data-testid="price-option-heading">{heading}</div>
        {cards}
      </>
    ) : cards[0];
  }

  // === Add-on ===
  if (checkoutGroupItem.item_type === 'AddOn') {
    const addOn = (visibleAddOns || []).find((ao) => ao.id === checkoutGroupItem.item_id);
    if (!addOn) return null;

    const isSoldOut = addOn.available_tickets === 0 || addOn.sold_out;

    return (
      <div className={`cwb-public-item-card ${isSoldOut ? 'is-disabled' : ''}`}>
        {addOn.image_thumb_url && (
          <div className="cwb-public-item-thumb">
            <img src={addOn.image_thumb_url} alt={addOn.name} />
          </div>
        )}
        <div className="cwb-public-item-main">
          <div className="cwb-public-item-name">{addOn.name}</div>
          <div className="cwb-public-item-price">
            {formatPrice(addOn.calculated_price, addOn.free)}
            {showAllInFee && addOn.all_in_price_description && (
              <span className="cwb-public-item-price-suffix">{` ${addOn.all_in_price_description}`}</span>
            )}
          </div>
          {addOn.description && (
            <div
              className="cwb-public-item-desc"
              dangerouslySetInnerHTML={{ __html: addOn.simple_format_description }}
            />
          )}
        </div>
        <div className="cwb-public-item-action">
          {isSoldOut ? (
            <SoldOutBadge />
          ) : (
            <QuantityStepper
              ariaLabel="Add-on Quantity"
              value={addOn.quantity || 0}
              options={addOnQuantityOptions(addOn, ticketTypes, userContext, startAtAddOns)}
              onChange={(quantity) => {
                const updated = addOns.map((ao) =>
                  ao.id === addOn.id ? { ...ao, quantity } : ao
                );

                if (quantity > 0) {
                  const updatedItem = { ...addOn, quantity };
                  const items = formatItemsForGoogleTagManager(confirm, promoCode, [], [updatedItem]);
                  postToParent('ga.add_to_cart', items);
                }

                addOnsChanged(updated);
                buildTicketReservation(csrfToken, confirm, ticketTypes, promoCode, updated, ticketReservation, userContext);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

CustomCheckoutGroupItemRow.propTypes = {
  team: PropTypes.object,
  adminPreview: PropTypes.bool,
  checkoutGroupItem: PropTypes.object.isRequired,
  checkoutGroupItems: PropTypes.array,
  visibleTickets: PropTypes.array,
  promoCode: PropTypes.object,
  ticketTypes: PropTypes.array,
  formatItemsForGoogleTagManager: PropTypes.func,
  postToParent: PropTypes.func,
  updateTicketTypeQuantity: PropTypes.func,
  buildTicketReservation: PropTypes.func,
  csrfToken: PropTypes.string,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  userContext: PropTypes.object,
  confirm: PropTypes.object.isRequired,
  addOnsChanged: PropTypes.func,
  addOnQuantityOptions: PropTypes.func,
  visibleAddOns: PropTypes.array,
  seatedAddOns: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  selectedObjectLabel: PropTypes.func,
  selectedObjectType: PropTypes.func,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func,
  onSelectionChange: PropTypes.func,
  no_orphan_seats: PropTypes.bool,
  visibleTicketTypes: PropTypes.func,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  ticketTypesChanged: PropTypes.func,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func,
  debouncedBuildTicketReservation: PropTypes.func,
  feeRules: PropTypes.array,
  donationTitle: PropTypes.string,
  donationCoverProcessingText: PropTypes.string,
  startAtAddOns: PropTypes.bool,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  selectedMembershipProductChanged: PropTypes.func,
};

CustomCheckoutGroupItemRow.defaultProps = {
  checkoutGroupItems: null,
  team: null,
  adminPreview: null,
  visibleTickets: null,
  promoCode: null,
  ticketTypes: null,
  formatItemsForGoogleTagManager: null,
  postToParent: null,
  updateTicketTypeQuantity: null,
  buildTicketReservation: null,
  csrfToken: null,
  addOns: null,
  ticketReservation: null,
  userContext: null,
  addOnsChanged: null,
  addOnQuantityOptions: null,
  visibleAddOns: null,
  seatedAddOns: null,
  seatsIOPublicKey: null,
  selectedObjectLabel: null,
  selectedObjectType: null,
  seatsIOChart: null,
  seatsIOChartChanged: null,
  seatsIOSelectedObjects: null,
  seatsIOSelectedObjectsChanged: null,
  onSelectionChange: null,
  no_orphan_seats: null,
  visibleTicketTypes: null,
  announceListEnabled: null,
  waitlistEnabled: null,
  ticketTypesChanged: null,
  donationCustomChargeType: null,
  donationCustomChargeTypeChanged: null,
  debouncedBuildTicketReservation: null,
  feeRules: null,
  donationTitle: null,
  donationCoverProcessingText: null,
  startAtAddOns: null,
  membershipProducts: null,
  selectedMembershipProduct: null,
  selectedMembershipProductChanged: null,
};

export default CustomCheckoutGroupItemRow;
